/* ─── Helpers ─────────────────────────────────────────────────────────────── */

function extractYoutubeId(url) {
  if (!url) return null;
  const m = url.match(/(?:v=|youtu\.be\/|youtube\.com\/(?:embed|shorts|live)\/)([^&?/]+)/);
  return m ? m[1] : null;
}

function extractTikTokId(url) {
  if (!url) return null;
  const m = url.match(/(?:\/video\/|\/embed\/v2\/)(\d+)/);
  return m ? m[1] : null;
}

function extractInstagramId(url) {
  if (!url) return null;
  const m = url.match(/instagram\.com\/(?:p|reel|tv)\/([\w-]+)/);
  return m ? m[1] : null;
}

function detectPlatform(url) {
  if (!url) return null;
  const u = url.toLowerCase();
  if (u.includes('youtube.com') || u.includes('youtu.be')) return 'youtube';
  if (u.includes('tiktok.com')) return 'tiktok';
  if (u.includes('instagram.com')) return 'instagram';
  if (u.includes('facebook.com') || u.includes('fb.watch')) return 'facebook';
  return null;
}

function decodeEmbedUrl(url) {
  return url ? url.replace(/&amp;/g, '&') : null;
}

const iframeStyle = { width: '100%', height: '100%', border: 'none' };

/* ─── Platform players ────────────────────────────────────────────────────── */

function VideoLinkFallback({ url, platform }) {
  if (!url) return null;
  return (
    <div style={{ marginBottom: 24, textAlign: 'center', padding: '40px 20px', background: '#f8f8f8', borderRadius: 12 }}>
      <div style={{ fontSize: 14, color: '#555', marginBottom: 16 }}>
        Cette vidéo ne peut pas être intégrée directement.
      </div>
      <a href={url} target="_blank" rel="noreferrer" className="btn-mcm" style={{ display: 'inline-block' }}>
        ▶ Regarder sur {platform || 'la plateforme'}
      </a>
    </div>
  );
}

function YouTubePlayer({ video, fallbackUrl }) {
  const embedUrl = decodeEmbedUrl(video?.primary_embed_url);
  const videoId =
    extractYoutubeId(embedUrl) ||
    extractYoutubeId(video?.lien_primaire) ||
    extractYoutubeId(fallbackUrl);

  if (!videoId) return <VideoLinkFallback url={video?.lien_primaire || fallbackUrl} platform="YouTube" />;

  return (
    <div className="direct-embed-frame">
      <iframe
        className="video-embed"
        src={`https://www.youtube.com/embed/${videoId}`}
        title={video?.theme || video?.date || 'YouTube video'}
        allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
        allowFullScreen
        loading="lazy"
      />
    </div>
  );
}

function FacebookPlayer({ video, fallbackUrl, dashboardMode }) {
  const link = video?.lien_primaire || fallbackUrl || '';
  const isReel = /\/reel\/|\/reels\//i.test(link);
  const isShareLink = link.includes('facebook.com/share/');

  if (isShareLink) return <VideoLinkFallback url={link} platform="Facebook" />;

  const embedUrl =
    decodeEmbedUrl(video?.primary_embed_url) ||
    (video?.primary_video_id
      ? `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(`https://www.facebook.com/watch/?v=${video.primary_video_id}`)}&show_text=false&width=500`
      : link
        ? `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(link)}&show_text=false&width=500`
        : null);

  if (!embedUrl) return <VideoLinkFallback url={link} platform="Facebook" />;

  const reelHeight = dashboardMode ? 400 : 560;

  return isReel ? (
    <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 24 }}>
      <iframe
        src={embedUrl}
        title={video?.theme || video?.date || 'Facebook Reel'}
        width="314"
        height={reelHeight}
        style={{ border: 'none', borderRadius: 8, maxWidth: '100%' }}
        allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
        allowFullScreen
        loading="lazy"
      />
    </div>
  ) : (
    <div className="direct-embed-frame">
      <iframe
        className="video-embed"
        src={embedUrl}
        title={video?.theme || video?.date || 'Facebook video'}
        allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
        allowFullScreen
        loading="lazy"
        style={iframeStyle}
      />
    </div>
  );
}

function TikTokPlayer({ video, fallbackUrl, dashboardMode }) {
  const embedUrl = decodeEmbedUrl(video?.primary_embed_url);
  const tiktokId =
    (video?.primary_video_id && /^\d+$/.test(video.primary_video_id) ? video.primary_video_id : null) ||
    extractTikTokId(embedUrl) ||
    extractTikTokId(video?.lien_primaire) ||
    extractTikTokId(fallbackUrl);

  const src = tiktokId ? `https://www.tiktok.com/embed/v2/${tiktokId}` : embedUrl || null;

  if (!src) return <VideoLinkFallback url={video?.lien_primaire || fallbackUrl} platform="TikTok" />;

  const height = dashboardMode ? 400 : undefined;

  return (
    <div style={{ width: '100%', maxWidth: 325, margin: '0 auto 24px', aspectRatio: height ? undefined : '9 / 16', height, borderRadius: 12, overflow: 'hidden', background: '#000' }}>
      <iframe
        style={iframeStyle}
        src={src}
        title={video?.theme || video?.date || 'TikTok video'}
        allow="autoplay; encrypted-media; picture-in-picture; web-share"
        allowFullScreen
        scrolling="no"
        loading="lazy"
      />
    </div>
  );
}

function InstagramPlayer({ video, fallbackUrl, dashboardMode }) {
  const shortcode = extractInstagramId(video?.lien_primaire) || extractInstagramId(fallbackUrl);
  const embedUrl = shortcode ? `https://www.instagram.com/p/${shortcode}/embed/` : null;

  if (!embedUrl) return <VideoLinkFallback url={video?.lien_primaire || fallbackUrl} platform="Instagram" />;

  const height = dashboardMode ? 400 : 560;

  return (
    <div style={{ width: '100%', maxWidth: 480, margin: '0 auto 24px', borderRadius: 12, overflow: 'hidden', background: '#fff', minHeight: dashboardMode ? 'unset' : 480 }}>
      <iframe
        style={{ ...iframeStyle, height }}
        src={embedUrl}
        title={video?.theme || video?.date || 'Instagram video'}
        allow="autoplay; encrypted-media; picture-in-picture; web-share"
        allowFullScreen
        scrolling="no"
        loading="lazy"
      />
    </div>
  );
}

/* ─── VideoPreview orchestrator ───────────────────────────────────────────── */

export function VideoPreview({ video, fallbackUrl, compact, stretch, dashboardMode }) {
  if (!video && !fallbackUrl) return null;

  const recUrl = video?.recording_url;
  const isAudio = recUrl && /\.(mp3|wav|ogg|aac|m4a|flac)(\b|$)/i.test(recUrl.split('?')[0]);
  const isRecordedVideo = recUrl && /\.(mp4|mov|avi|webm|mkv|m4v)(\b|$)/i.test(recUrl.split('?')[0]);

  if (isAudio) {
    return (
      <audio controls src={recUrl} style={{ width: '100%', borderRadius: 6 }}>
        Votre navigateur ne supporte pas la lecture audio.
      </audio>
    );
  }

  if (isRecordedVideo) {
    return (
      <video controls src={recUrl} style={{ width: '100%', borderRadius: 8, maxHeight: compact ? 135 : 480, background: '#000' }}>
        Votre navigateur ne supporte pas la lecture vidéo.
      </video>
    );
  }

  const platform =
    video?.primary_platform ||
    detectPlatform(video?.lien_primaire) ||
    detectPlatform(fallbackUrl);

  // In compact mode, render a fixed 16:9 iframe box for all platforms
  if (compact) {
    const link = video?.lien_primaire || fallbackUrl || '';
    const isShareLink = link.includes('facebook.com/share/');

    let src = null;
    if (platform === 'youtube') {
      const embedUrl = decodeEmbedUrl(video?.primary_embed_url);
      const videoId = extractYoutubeId(embedUrl) || extractYoutubeId(link) || extractYoutubeId(fallbackUrl);
      if (videoId) src = `https://www.youtube.com/embed/${videoId}`;
    } else if (platform === 'facebook' && !isShareLink) {
      src = decodeEmbedUrl(video?.primary_embed_url) ||
        (video?.primary_video_id
          ? `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(`https://www.facebook.com/watch/?v=${video.primary_video_id}`)}&show_text=false&width=500`
          : link ? `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(link)}&show_text=false&width=500` : null);
    } else if (platform === 'tiktok') {
      const embedUrl = decodeEmbedUrl(video?.primary_embed_url);
      const tiktokId =
        (video?.primary_video_id && /^\d+$/.test(video.primary_video_id) ? video.primary_video_id : null) ||
        extractTikTokId(embedUrl) || extractTikTokId(link) || extractTikTokId(fallbackUrl);
      if (tiktokId) src = `https://www.tiktok.com/embed/v2/${tiktokId}`;
      else src = embedUrl || null;
    } else if (platform === 'instagram') {
      const shortcode = extractInstagramId(link) || extractInstagramId(fallbackUrl);
      if (shortcode) src = `https://www.instagram.com/p/${shortcode}/embed/`;
    } else {
      src = decodeEmbedUrl(video?.primary_embed_url);
    }

    const containerStyle = { width: 240, height: 135, borderRadius: 8, overflow: 'hidden', background: '#000', flexShrink: 0 };

    // TikTok & Instagram can't render in a small box — show branded fallback
    if (platform === 'tiktok' || platform === 'instagram') {
      const platformLabel = platform === 'tiktok' ? 'TikTok' : 'Instagram';
      const bgColor = platform === 'tiktok' ? '#010101' : '#833ab4';
      const icon = platform === 'tiktok' ? '♪' : '📷';
      return (
        <div style={{ ...containerStyle, background: bgColor, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
          <span style={{ fontSize: 22 }}>{icon}</span>
          <span style={{ fontSize: 11, color: '#fff', fontWeight: 600 }}>{platformLabel}</span>
          <a href={link || video?.lien_primaire} target="_blank" rel="noreferrer"
            style={{ fontSize: 10, color: 'rgba(255,255,255,0.75)', textDecoration: 'underline' }}>
            Voir la vidéo
          </a>
        </div>
      );
    }

    if (!src) {
      return (
        <div style={{ ...containerStyle, background: '#f0f0f0', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
          <span style={{ fontSize: 12, color: '#888', textAlign: 'center', padding: '0 12px' }}>Aperçu non disponible</span>
          {link && <a href={link} target="_blank" rel="noreferrer" style={{ fontSize: 11, color: '#8b1512' }}>Ouvrir le lien</a>}
        </div>
      );
    }

    return (
      <div style={containerStyle}>
        <iframe
          src={src}
          title={video?.theme || 'Aperçu vidéo'}
          width="100%"
          height="100%"
          style={{ border: 'none', display: 'block' }}
          allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
          allowFullScreen
          scrolling="no"
          loading="lazy"
        />
      </div>
    );
  }

  // dashboardMode: render everything in a fixed container, no scrolling, no overflow
  if (dashboardMode) {
    const link = video?.lien_primaire || fallbackUrl || '';
    const isReel = /\/reel\/|\/reels\//i.test(link);
    const isShareLink = link.includes('facebook.com/share/');
    const isVertical = (platform === 'facebook' && isReel) || platform === 'tiktok' || platform === 'instagram';

    let src = null;
    if (platform === 'youtube') {
      const id = extractYoutubeId(decodeEmbedUrl(video?.primary_embed_url)) || extractYoutubeId(link);
      if (id) src = `https://www.youtube.com/embed/${id}`;
    } else if (platform === 'facebook' && !isShareLink) {
      src = decodeEmbedUrl(video?.primary_embed_url) ||
        (video?.primary_video_id
          ? `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(`https://www.facebook.com/watch/?v=${video.primary_video_id}`)}&show_text=false&width=500`
          : `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(link)}&show_text=false&width=500`);
    } else if (platform === 'tiktok') {
      const id = (video?.primary_video_id && /^\d+$/.test(video.primary_video_id) ? video.primary_video_id : null) ||
        extractTikTokId(decodeEmbedUrl(video?.primary_embed_url)) || extractTikTokId(link);
      src = id ? `https://www.tiktok.com/embed/v2/${id}` : decodeEmbedUrl(video?.primary_embed_url);
    } else if (platform === 'instagram') {
      const sc = extractInstagramId(link);
      if (sc) src = `https://www.instagram.com/p/${sc}/embed/`;
    } else {
      src = decodeEmbedUrl(video?.primary_embed_url);
    }

    if (!src) return <VideoLinkFallback url={link} platform={platform || 'la plateforme'} />;

    if (isVertical) {
      return (
        <div style={{ display: 'flex', justifyContent: 'center', width: '100%', height: '100%', background: '#000' }}>
          <div style={{ width: 'auto', height: '100%', aspectRatio: '9 / 16', overflow: 'hidden' }}>
            <iframe
              src={src}
              title={video?.theme || 'Vidéo'}
              width="100%"
              height="100%"
              style={{ border: 'none', display: 'block' }}
              allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
              allowFullScreen
              scrolling="no"
              loading="lazy"
            />
          </div>
        </div>
      );
    }

    return (
      <div style={{ width: '100%', height: '100%', background: '#000' }}>
        <iframe
          src={src}
          title={video?.theme || 'Vidéo'}
          style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
          allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
          allowFullScreen
          scrolling="no"
          loading="lazy"
        />
      </div>
    );
  }

  switch (platform) {
    case 'youtube':   return <YouTubePlayer video={video} fallbackUrl={fallbackUrl} />;
    case 'facebook':  return <FacebookPlayer video={video} fallbackUrl={fallbackUrl} />;
    case 'tiktok':    return <TikTokPlayer video={video} fallbackUrl={fallbackUrl} />;
    case 'instagram': return <InstagramPlayer video={video} fallbackUrl={fallbackUrl} />;
    default: {
      const embedUrl = decodeEmbedUrl(video?.primary_embed_url);
      if (embedUrl) {
        return (
          <div className="direct-embed-frame">
            <iframe
              className="video-embed"
              src={embedUrl}
              title={video?.theme || video?.date || 'Vidéo'}
              allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
              allowFullScreen
              loading="lazy"
            />
          </div>
        );
      }
      return <VideoLinkFallback url={video?.lien_primaire || fallbackUrl} platform="la plateforme" />;
    }
  }
}
